import type { User } from '../types';

const STORAGE_KEY = 'oemstream_auth_user';

// Cloudflare Access headers that will be passed through
// In production, these come from Cloudflare Zero Trust
const CF_HEADERS = {
  email: 'cf-access-authenticated-user-email',
  id: 'cf-access-authenticated-user-id',
};

class AuthManager {
  private user: User | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.user = JSON.parse(stored);
      }
    } catch {
      this.user = null;
    }
  }

  // Explicit admin allowlist — every other user is read-only.
  private static readonly ADMIN_EMAILS = [
    'admin@oemstream.com',
    'dan.kelly@oemstream.com',
    'dan.moretti@oemstream.com',
  ];

  private isAdminEmail(email: string): boolean {
    return AuthManager.ADMIN_EMAILS.includes(email.toLowerCase());
  }

  // Set user from Cloudflare Access headers (called on page load in production)
  setUserFromHeaders(headers: Record<string, string>): User | null {
    const email = headers[CF_HEADERS.email];
    if (!email) return null;

    const user: User = {
      email,
      name: email.split('@')[0],
      isAdmin: this.isAdminEmail(email),
    };

    this.user = user;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    return user;
  }

  // For development: mock login
  mockLogin(email: string, isAdmin: boolean = false): User {
    const user: User = {
      email,
      name: email.split('@')[0],
      isAdmin: isAdmin || this.isAdminEmail(email),
    };
    this.user = user;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    return user;
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.user;
  }

  isAdmin(): boolean {
    return this.user?.isAdmin ?? false;
  }

  logout(): void {
    this.user = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  // In production with Cloudflare Pages + Access:
  // The user's email is available in request headers after authentication
  // No local login needed - Access handles it all
  async fetchUserFromEdge(): Promise<User | null> {
    try {
      // This endpoint would be a Cloudflare Pages Function
      // that reads CF-Access headers and returns user info
      const response = await fetch('/api/auth/user');
      if (response.ok) {
        const data = await response.json();
        if (data.email) {
          return this.setUserFromHeaders({
            [CF_HEADERS.email]: data.email,
            [CF_HEADERS.id]: data.id || '',
          });
        }
      }
    } catch {
      // Edge function not available (local dev)
    }
    return null;
  }
}

export const auth = new AuthManager();
export default AuthManager;
